import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Api from "../utils/api";
import loginTranslations from "../locales/loginTranslations";
import { LanguageContext } from "../locales/LanguageContext";
import {
  Box,
  Card,
  Typography,
  InputAdornment,
  TextField,
  Button,
  Divider,
} from "@mui/material";
import { Email, Lock } from "@mui/icons-material";

const Login = () => {
  const { lang } = useContext(LanguageContext);
  const t = loginTranslations[lang] || {};
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const data = await Api.post("auth/login", form);
      localStorage.setItem("token", data.token);
      setSuccess(t.success);
      setForm({ email: "", password: "" });
      // Role-based redirect
      const userRole = data.user?.role || data.user?.roleId?.name;
      if (userRole === "Admin") {
        navigate("/admin/dashboard");
      } else {
        navigate("/profile");
      }
    } catch (err) {
      setError(err.message || t.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t.pageTitle} | AKAAL KIDS</title>
      </Helmet>
      {/* Animated gradient header */}
      <Box
        sx={{
          width: "100%",
          background:
            "linear-gradient(270deg, #f6815e, #f6b93b, #fffbe6, #f6815e)",
          backgroundSize: "600% 600%",
          color: "#fff",
          py: 5,
          textAlign: "center",
          mb: 4,
          boxShadow: "0 4px 24px #f6815e22",
          position: "relative",
          overflow: "hidden",
          animation: "gradientBG 12s ease infinite",
          "@keyframes gradientBG": {
            "0%": { backgroundPosition: "0% 50%" },
            "50%": { backgroundPosition: "100% 50%" },
            "100%": { backgroundPosition: "0% 50%" },
          },
        }}
      >
        <Typography
          variant="h2"
          fontWeight={900}
          sx={{ letterSpacing: 1, mb: 1, textShadow: "0 2px 12px #f6815e55" }}
        >
          {t.heroTitle}
        </Typography>
        <Typography variant="h5" fontWeight={500} sx={{ opacity: 0.96 }}>
          <Lock sx={{ verticalAlign: "middle", mr: 1 }} />
          {t.heroDesc}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{ mt: 2, fontStyle: "italic", opacity: 0.9 }}
        >
          {"Welcome back! Every login is a new opportunity to learn."}
        </Typography>
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="40vh"
      >
        <Card
          sx={{
            p: 4,
            borderRadius: 4,
            boxShadow: 12,
            minWidth: 340,
            maxWidth: 400,
            width: "100%",
            transition: "transform 0.25s cubic-bezier(.4,2,.6,1)",
            "&:hover": { transform: "scale(1.025)", boxShadow: 20 },
          }}
        >
          <form onSubmit={handleSubmit}>
            <TextField
              type="email"
              name="email"
              label={t.emailPlaceholder}
              value={form.email}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: "#f6815e" }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              type="password"
              name="password"
              label={t.passwordPlaceholder}
              value={form.password}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: "#f6b93b" }} />
                  </InputAdornment>
                ),
              }}
            />
            {error && (
              <Typography color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
            {success && (
              <Typography color="success.main" sx={{ mt: 1 }}>
                {success}
              </Typography>
            )}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                mt: 3,
                mb: 2,
                fontWeight: 700,
                background: "linear-gradient(90deg,#f6815e,#f6b93b)",
                boxShadow: 2,
                "&:hover": {
                  background: "linear-gradient(90deg,#f6b93b,#f6815e)",
                },
              }}
              disabled={loading}
            >
              {loading ? t.loggingIn : t.loginBtn}
            </Button>
            <Divider sx={{ my: 2 }} />
            <Box textAlign="center">
              <a
                href="/forgot-password"
                style={{
                  color: "#f6815e",
                  fontWeight: 500,
                  textDecoration: "none",
                }}
              >
                {t.forgotPassword}
              </a>
            </Box>
          </form>
        </Card>
      </Box>
    </>
  );
};
export default Login;
